import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Loader2, BookOpen, Plus, Search, FileText, ExternalLink } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import oneSpaceApi from '../../lib/onespace-api';

// Wiki item interface
interface WikiItem {
  id: string;
  title: string;
  slug: string;
  content?: string;
  excerpt?: string;
  team_id: string | number | null;
  team_name?: string;
  author_name?: string;
  created_at: string;
  updated_at: string;
  is_published: boolean;
  view_count?: number;
}

// Widget props interface
interface WikiWidgetProps {
  id: string;
  title?: string;
  team_id?: string | number | null;
  max_items?: number;
  show_create_button?: boolean;
  compact_mode?: boolean;
}

const WikiWidget: React.FC<WikiWidgetProps> = ({
  id,
  title = "Team Wiki",
  team_id = null,
  max_items = 5,
  show_create_button = true,
  compact_mode = false
}) => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [wikiItems, setWikiItems] = useState<WikiItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Ref to track previous props to debug unnecessary re-renders
  const prevPropsRef = useRef({ team_id, max_items, isAuthenticated, searchTerm });

  // Debug: Log when props change
  useEffect(() => {
    const prev = prevPropsRef.current;
    const current = { team_id, max_items, isAuthenticated, searchTerm };
    
    if (JSON.stringify(prev) !== JSON.stringify(current)) {
      console.log('WikiWidget props changed:', {
        previous: prev,
        current: current,
        widgetId: id
      });
      prevPropsRef.current = current;
    }
  });

  // Memoize the API parameters
  const apiParams = useMemo(() => {
    const params: any = {
      limit: max_items,
      published: true
    };
    
    if (team_id) {
      params.team_id = team_id;
    }
    
    if (searchTerm) {
      params.search = searchTerm;
    }
    
    return params;
  }, [max_items, team_id, searchTerm]);

  // Fetch wiki items with useCallback to prevent unnecessary re-creations
  const fetchWikiItems = useCallback(async () => {
    if (!isAuthenticated) return;
    
    console.log('WikiWidget fetchWikiItems called for widget:', id, 'with params:', apiParams);
    
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await oneSpaceApi.get('/api/2wikicontent', { params: apiParams });
      
      let items: WikiItem[] = [];
      if (response.data) {
        if (Array.isArray(response.data)) {
          items = response.data;
        } else if (Array.isArray(response.data.data)) {
          items = response.data.data;
        }
      }
      
      setWikiItems(items);
    } catch (err) {
      console.error('Error fetching wiki items:', err);
      setError('Kon wiki items niet laden.');
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, apiParams, id]);

  // Load wiki items on mount and when dependencies change
  useEffect(() => {
    fetchWikiItems();
  }, [fetchWikiItems]);

  // Handle create new wiki item
  const handleCreateWiki = () => {
    // This would typically open a modal or navigate to create page
    if (isAuthenticated) {
      // For now, just show an alert - can be replaced with actual navigation
      alert('Nieuwe wiki pagina maken - functionaliteit wordt binnenkort toegevoegd');
    }
  };

  // Handle view wiki item - navigate to wiki page
  const handleViewWiki = (item: WikiItem) => {
    navigate(`/wiki/${item.id}`);
  };

  // Format date for display
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return 'Vandaag';
    } else if (diffDays === 1) {
      return 'Gisteren';
    } else if (diffDays < 7) {
      return `${diffDays} dagen geleden`;
    } else {
      return date.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'short'
      });
    }
  };

  // Demo data for when not authenticated - memoized to prevent re-creation
  const demoWikiItems: WikiItem[] = useMemo(() => [
    {
      id: '1',
      title: 'Onboarding Proces',
      slug: 'onboarding-proces',
      excerpt: 'Volledige gids voor het inwerken van nieuwe teamleden...',
      team_id: 1,
      team_name: 'HR Team',
      author_name: 'Sarah van der Berg',
      created_at: '2024-01-15T10:00:00Z',
      updated_at: '2024-01-20T14:30:00Z',
      is_published: true,
      view_count: 42
    },
    {
      id: '2',
      title: 'API Documentatie',
      slug: 'api-documentatie',
      excerpt: 'Technische documentatie voor de OneSpace API endpoints...',
      team_id: 2,
      team_name: 'Development',
      author_name: 'Mike Johnson',
      created_at: '2024-01-10T09:15:00Z',
      updated_at: '2024-01-18T16:45:00Z',
      is_published: true,
      view_count: 156
    },
    {
      id: '3',
      title: 'Marketing Strategie Q1',
      slug: 'marketing-strategie-q1',
      excerpt: 'Overzicht van marketing doelen en tactieken voor Q1 2024...',
      team_id: 3,
      team_name: 'Marketing',
      author_name: 'Lisa Chen',
      created_at: '2024-01-05T11:20:00Z',
      updated_at: '2024-01-15T13:10:00Z',
      is_published: true,
      view_count: 89
    }
  ], []);

  const displayItems = isAuthenticated ? wikiItems : demoWikiItems.slice(0, max_items);

  return (
    <div className="h-full bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            {title}
          </h3>
        </div>
        
        {show_create_button && (
          <Button
            onClick={handleCreateWiki}
            size="sm"
            className="h-8 px-3"
          >
            <Plus className="h-4 w-4 mr-1" />
            Nieuw
          </Button>
        )}
      </div>

      {/* Search */}
      {!compact_mode && (
        <div className="relative mb-4">
          <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Zoek in wiki..."
            className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-800 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
            <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
              Wiki laden...
            </span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-24">
            <div className="text-center">
              <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          </div>
        ) : displayItems.length === 0 ? (
          <div className="flex items-center justify-center h-24">
            <div className="text-center">
              <BookOpen className="h-8 w-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {searchTerm ? 'Geen wiki items gevonden' : 'Nog geen wiki items'}
              </p>
              {!searchTerm && show_create_button && (
                <Button
                  onClick={handleCreateWiki}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                >
                  Eerste wiki maken
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {displayItems.map((item) => (
              <div
                key={item.id}
                onClick={() => handleViewWiki(item)}
                className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-800 dark:text-white truncate group-hover:text-primary transition-colors">
                        {item.title}
                      </h4>
                      <ExternalLink className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    
                    {!compact_mode && item.excerpt && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 mb-2">
                        {item.excerpt}
                      </p>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-500">
                      <span>{formatDate(item.updated_at)}</span>
                      {item.author_name && (
                        <span>door {item.author_name}</span>
                      )}
                      {item.view_count && (
                        <span>{item.view_count} weergaven</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      {displayItems.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
          <Button
            variant="ghost"
            size="sm"
            className="w-full text-gray-600 dark:text-gray-400 hover:text-primary"
            onClick={() => alert('Alle wiki items bekijken - functionaliteit wordt binnenkort toegevoegd')}
          >
            Alle wiki items bekijken →
          </Button>
        </div>
      )}

      {/* Auth notice */}
      {!isAuthenticated && (
        <div className="mt-3 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
          <p className="text-xs text-blue-700 dark:text-blue-400 text-center">
            Log in om team wiki te beheren
          </p>
        </div>
      )}
    </div>
  );
};

export default WikiWidget; 